/**
 * Rol/yetki yardımcıları.
 *
 * Yetkiler `localStorage.roles` üzerinden okunur. Backend
 * `LoginService.GetPermissionsForUser` `General.PermissionInfo.Value` kolonunu
 * döndürür; aşağıdaki sabitler o değerlerle birebir eşleşmelidir.
 */

export const PERMISSIONS = {
  WISER_CANDIDATE_TRACKING: "WiserCandidateTracking",
  PROJECT: "Project",
  CANDIDATE: "Candidate",
  WISER_NEW_APPLICATIONS: "WiserNewApplications",
  WISER_CANDIDATE_TRANSFER: "WiserCandidateTransfer",
  PERSONEL_DETAIL: "PersonelDetail",
  PERSONNAL_CV: "PersonnalCv",
  PERSONEL_MEETING: "PersonelMeeting",
  REPORT_NEGATIVE_CANDIDATES: "ReportNegativeCandidates",
  REPORT_REJECT_REASONS: "ReportRejectReasons",
  REPORT_KVKK_STATUS: "ReportKvkkStatus",
  REPORT_HR_MANAGEMENT: "ReportHrManagement",
  REPORT_TALENT_SPOT: "ReportTalentSpot",
  REPORT_PLACEMENT: "ReportPlacement",
  REPORT_WISER_KPI: "ReportWiserKpi",
  REPORT_USAGE_STATISTICS: "UsageStatistics",
  VERSION_ADMIN: "versionAdmin",
  PERSONEL_DELETE: "PersonelDelete",
  PERSONEL_STATE_DELETE: "PersonelStateDelete",
  POSITION_DELETE: "PositionDelete",
  PERSONEL_GENERAL: "PersonelGeneral",
  PERSONEL_SKILLS: "PersonelSkills",
  PERSONEL_DOCUMENTS: "PersonelDocuments",
  PERSONEL_NOTES: "PersonelNotes",
  USER_ADMIN: "UserAdmin",
  CONFIGURATION: "ConfigurationPermission",
  MASTER_DATA_ADMIN: "MasterDataAdmin",
} as const;

/**
 * "Raporlar" üst menüsünün görünürlüğü için — en az biri yeterli.
 * Yerleştirme ve Wiser KPI raporları henüz bu projede yok; ekranlar
 * eklendiğinde ilgili menü kalemleri de bu listeyi kullanacak.
 */
export const REPORT_PERMISSIONS: string[] = [
  PERMISSIONS.REPORT_NEGATIVE_CANDIDATES,
  PERMISSIONS.REPORT_REJECT_REASONS,
  PERMISSIONS.REPORT_KVKK_STATUS,
  PERMISSIONS.REPORT_HR_MANAGEMENT,
  PERMISSIONS.REPORT_TALENT_SPOT,
  PERMISSIONS.REPORT_PLACEMENT,
  PERMISSIONS.REPORT_WISER_KPI,
  PERMISSIONS.REPORT_USAGE_STATISTICS,
];

/**
 * `localStorage.roles` hem JSON dizi hem virgülle ayrılmış düz string olabilir.
 * Backend `string[]` döner, `localStorage.setItem` bunu "A,B,C" haline getirir.
 */
export const parseRoles = (rolesRaw: string | null): string[] => {
  if (!rolesRaw) return [];
  try {
    const parsed = JSON.parse(rolesRaw);
    if (Array.isArray(parsed)) return parsed.map(String);
  } catch {
    // roles claim is a plain string in most sessions
  }
  return rolesRaw
    .split(",")
    .map((r) => r.trim())
    .filter(Boolean);
};

/** Tam eşleşme yapar — `roles.includes("X")` substring kontrolünün yerini alır. */
export const hasRole = (rolesRaw: string | null, role: string): boolean =>
  parseRoles(rolesRaw).includes(role);

export const hasAnyRole = (rolesRaw: string | null, roles: string[]): boolean => {
  const parsed = parseRoles(rolesRaw);
  return roles.some((role) => parsed.includes(role));
};

export interface MenuAccessConfig {
  role?: string;
  roles?: string[];
}

/** Yetki tanımlanmamış menüler herkese açıktır. */
export const isMenuItemVisible = (
  item: MenuAccessConfig,
  rolesRaw: string | null,
): boolean => {
  if (item.role) return hasRole(rolesRaw, item.role);
  if (item.roles?.length) return hasAnyRole(rolesRaw, item.roles);
  return true;
};

export interface MenuItemConfig extends MenuAccessConfig {
  id: number;
  label: string;
  url: string;
  icon: string;
  activeIcon?: string;
  disableActiveTypography?: boolean;
  activeOnSubmenuRoute?: boolean;
  tooltip?: string;
  hidden?: boolean;
  disabled?: boolean;
  submenu?: SubmenuItemConfig[];
}

export interface SubmenuItemConfig extends MenuAccessConfig {
  id: number;
  label: string;
  url: string;
}

/** Alt öğeleri filtreler; görünür alt öğe kalmazsa üst menüyü de gizler. */
export const filterMenusByRole = (
  menus: MenuItemConfig[],
  rolesRaw: string | null,
): MenuItemConfig[] =>
  menus
    .map((menu) => {
      if (menu.submenu?.length) {
        const visibleSubmenu = menu.submenu.filter((item) =>
          isMenuItemVisible(item, rolesRaw),
        );
        if (visibleSubmenu.length === 0) return null;
        return { ...menu, submenu: visibleSubmenu };
      }

      if (!isMenuItemVisible(menu, rolesRaw)) return null;
      return menu;
    })
    .filter((menu): menu is MenuItemConfig => menu !== null);

export interface CandidateDetailTabConfig {
  key: string;
  permission: string;
}

export const CANDIDATE_DETAIL_TABS: CandidateDetailTabConfig[] = [
  { key: "Genel", permission: PERMISSIONS.PERSONEL_GENERAL },
  { key: "Yetkinlikler", permission: PERMISSIONS.PERSONEL_SKILLS },
  { key: "Durumlar", permission: PERMISSIONS.PERSONEL_MEETING },
];

export const getCandidateDetailTabs = (
  rolesRaw: string | null,
): CandidateDetailTabConfig[] =>
  CANDIDATE_DETAIL_TABS.filter((tab) => hasRole(rolesRaw, tab.permission));
